import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail, Save, Send, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface SmtpConfig {
  host: string;
  port: number;
  username: string;
  password_set: boolean;
  from_email: string;
  from_name: string;
  use_tls: boolean;
  updated_at?: string | null;
  updated_by?: string | null;
}

export const SmtpConfigCard: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<SmtpConfig>({
    queryKey: ["admin-smtp"],
    queryFn: async () => (await api.get("/admin/smtp")).data,
  });

  const [host, setHost] = React.useState("");
  const [port, setPort] = React.useState<number>(587);
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [fromEmail, setFromEmail] = React.useState("");
  const [fromName, setFromName] = React.useState("SociBaba");
  const [useTls, setUseTls] = React.useState(true);
  const [testTo, setTestTo] = React.useState("");
  const seeded = React.useRef(false);

  React.useEffect(() => {
    if (seeded.current || !q.data) return;
    seeded.current = true;
    setHost(q.data.host || "");
    setPort(q.data.port || 587);
    setUsername(q.data.username || "");
    setFromEmail(q.data.from_email || "");
    setFromName(q.data.from_name || "SociBaba");
    setUseTls(q.data.use_tls);
  }, [q.data]);

  const save = useMutation({
    mutationFn: async () => {
      const payload: any = { host, port, username, from_email: fromEmail, from_name: fromName, use_tls: useTls };
      if (password) payload.password = password;
      return (await api.put("/admin/smtp", payload)).data;
    },
    onSuccess: () => {
      toast.success("SMTP configuration saved");
      setPassword("");
      qc.invalidateQueries({ queryKey: ["admin-smtp"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "SMTP save failed"),
  });

  const test = useMutation({
    mutationFn: async () => (await api.post("/admin/smtp/test", { to: testTo })).data,
    onSuccess: (d) => toast.success(`Test email sent to ${d.to}`),
    onError: (e: any) => toast.error(e?.response?.data?.detail || "SMTP test failed"),
  });

  const digestPreview = useMutation({
    mutationFn: async () => (await api.post("/composer/admin/digest-test", { email: testTo || undefined })).data,
    onSuccess: (d) => toast.success(`Weekly digest preview sent to ${d.sent_to}`),
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Digest preview failed"),
  });

  const isConfigured = q.data && q.data.host && q.data.from_email && q.data.password_set;

  if (q.isLoading) {
    return (
      <div className="sg-card p-6 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading SMTP config…
      </div>
    );
  }

  return (
    <div className="sg-card p-6" data-testid="smtp-config-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
          <Mail className="h-5 w-5 text-orange-700" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="font-display text-lg font-semibold tracking-tight">
              SMTP configuration
            </div>
            {isConfigured ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                <CheckCircle2 className="h-3 w-3" /> Configured
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                <AlertCircle className="h-3 w-3" /> Not configured
              </span>
            )}
          </div>
          <p className="text-sm text-[color:var(--sg-muted)] mt-1">
            Used for the password-reset flow and (soon) email digests. When
            SMTP is not configured, password reset silently disables — users
            won't be able to recover forgotten passwords.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Host</Label>
          <Input data-testid="smtp-host" value={host} onChange={(e) => setHost(e.target.value)} className="mt-1.5 bg-white" placeholder="smtp.gmail.com" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Port</Label>
          <Input data-testid="smtp-port" type="number" value={port} onChange={(e) => setPort(parseInt(e.target.value || "587", 10))} className="mt-1.5 bg-white" placeholder="587" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Username</Label>
          <Input data-testid="smtp-username" value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1.5 bg-white" placeholder="you@domain.com" autoComplete="off" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Password / App token</Label>
          <Input
            data-testid="smtp-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 bg-white"
            placeholder={q.data?.password_set ? "•••••••• (saved — paste to replace)" : "SMTP password or app-specific token"}
            autoComplete="new-password"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">From email</Label>
          <Input data-testid="smtp-from-email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} className="mt-1.5 bg-white" placeholder="no-reply@socibaba.com" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">From name</Label>
          <Input data-testid="smtp-from-name" value={fromName} onChange={(e) => setFromName(e.target.value)} className="mt-1.5 bg-white" placeholder="SociBaba" />
        </div>
        <div className="md:col-span-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useTls}
              onChange={(e) => setUseTls(e.target.checked)}
              data-testid="smtp-use-tls"
              className="h-4 w-4 accent-[color:var(--sg-violet)]"
            />
            <span className="text-sm text-[color:var(--sg-fg)]">Use STARTTLS (recommended for port 587)</span>
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-6 pt-4 border-t border-[color:var(--sg-border)]">
        <Button
          data-testid="smtp-save"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
        >
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save configuration
        </Button>

        <div className="flex-1 flex items-center gap-2 min-w-[280px]">
          <Input
            data-testid="smtp-test-to"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="Send test email to…"
            className="bg-white flex-1"
          />
          <Button
            data-testid="smtp-test-btn"
            variant="outline"
            onClick={() => test.mutate()}
            disabled={test.isPending || !testTo.trim() || !isConfigured}
          >
            {test.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send test
          </Button>
          <Button
            data-testid="digest-preview-btn"
            variant="outline"
            onClick={() => digestPreview.mutate()}
            disabled={digestPreview.isPending || !isConfigured}
            title="Sends this week's AI digest preview to the address above (or your own if blank)"
            className="border-[color:var(--sb-saffron)] text-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron)]/10"
          >
            {digestPreview.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Send me a preview
          </Button>
        </div>
      </div>

      {q.data?.updated_at && (
        <div className="text-[11px] text-[color:var(--sg-muted)] mt-3">
          Last updated {new Date(q.data.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};
